import classNames from 'classnames';
import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import useRouter from 'use-react-router';
import { RequiredPlanBadge } from '../../../../../common/components/ProBadge';
import { getDeviceInfo } from '../../../../../common/hooks/use-device-info';
import { PageBillingType } from '@mm/state/interfaces/connected-page';
import { activeChatbotSelector } from '@mm/state/selectors/viewSelectors';
import { useEditPagePrimeState } from '../../../../common/hooks/use-edit-page-prime-state';
import { Heading } from '@mm/components';
import { EditStartBotHeader } from '../../common/StartBotPromoterPanel/EditStartBotHeader/EditStartBotHeader';
import { usePromoterActivator } from '../../common/use-promoter-activator';
import { ChatWidgetTroubleshootingTool } from '@mm/stateful-components';
import { CustomerChatAdvancedSettings } from './CustomerChatAdvancedSettings/CustomerChatAdvancedSettings';
import { CustomerChatAppearanceEditor } from './CustomerChatAppearanceEditor/CustomerChatAppearanceEditor';
import { CustomerChatDialogueEditor } from './CustomerChatDialogueEditor/CustomerChatDialogueEditor';
import './CustomerChatEditor.pcss';
import { CustomerChatInstallInformation } from './CustomerChatInstallInformation/CustomerChatInstallInformation';
import { CustomerChatPreview } from './CustomerChatPreview/CustomerChatPreview';
import { useCustomerChatWidgetPreviews } from './use-customer-chat-widget-previews';
import { ChatWidgetPreviewChannelOption } from './ChatWidgetPreviewFrame/ChatWidgetPreviewFrame';
import { useGreenCueMarkers } from '../../../../../common/hooks/use-green-cue-markers';
import { InactiveChannelAlertSmart } from '../../../campaigns/panels/DripCampaignDetailsPanel/InactiveChannelAlert';

const EDITOR_SECTIONS = [
  'install',
  'dialogue',
  'appearance',
  'advanced',
  'troubleshooting',
] as const;
type EditorSection = typeof EDITOR_SECTIONS[number];
const isEditorSection = (section: string): section is EditorSection =>
  !!EDITOR_SECTIONS.find(s => s === section);

export const CustomerChatEditor = () => {
  const { location, history } = useRouter();
  const { widgets, isFetching, page, promoter } = useEditPagePrimeState(
    'customer_chat',
    true,
  );
  const [showGreenCueForActivate, setShowGreenCueForActivate] = useState(false);

  const { isWebChatActivated } = useGreenCueMarkers();

  const chatbot = useSelector(activeChatbotSelector);

  const [channel, setChannel] = useState<ChatWidgetPreviewChannelOption>(
    chatbot.chatWidgetChannel === 'facebook' ? 'facebook' : 'web_chat',
  );

  const { isMobile } = getDeviceInfo();
  const { openPreviewModal } = useCustomerChatWidgetPreviews(promoter);
  const { activatePromoter } = usePromoterActivator(promoter);

  const slug = location.hash.substring(1);
  const activeEditorSection: EditorSection = isEditorSection(slug)
    ? slug
    : 'install';

  if (!promoter || !page || isFetching) {
    return null;
  }

  const sectionConfig: Record<
    EditorSection,
    {
      title: string;
      pro?: boolean;
      component: React.ReactNode;
      previewType?: 'widget';
    }
  > = {
    install: {
      title: 'Install Website Chat',
      component: (
        <CustomerChatInstallInformation
          promoter={promoter}
          setShowGreenCueForActivate={setShowGreenCueForActivate}
        />
      ),
      previewType: 'widget',
    },
    dialogue: {
      title: 'Edit Chat Dialogue',
      component: (
        <CustomerChatDialogueEditor
          promoter={promoter}
          widgets={widgets}
          page={page}
        />
      ),
    },
    appearance: {
      title: 'Edit Chat Icon & Greeting',
      component: (
        <CustomerChatAppearanceEditor
          promoter={promoter}
          setPreviewChannel={setChannel}
        />
      ),
      previewType: 'widget',
    },
    advanced: {
      title: 'Edit Display Rules',
      pro: true,
      component: <CustomerChatAdvancedSettings promoter={promoter} />,
    },
    troubleshooting: {
      title: 'Troubleshooting',
      component: <ChatWidgetTroubleshootingTool promoter={promoter} />,
    },
  };

  const { component: activeComponent, previewType: activePreviewType } =
    sectionConfig[activeEditorSection];

  const { chatWidgetChannel } = chatbot;
  const isOmniChat = chatWidgetChannel === 'omnichat';

  return (
    <div className="customer-chat-editor">
      <InactiveChannelAlertSmart
        facebook={isOmniChat || chatWidgetChannel === 'facebook'}
        webChat={isOmniChat || chatWidgetChannel === 'web_chat'}
        type="web chat"
      />

      <EditStartBotHeader
        promoter={promoter}
        page={page}
        botIsTemplate={!!chatbot.template}
        customPreviewAction={openPreviewModal}
        customActivateAction={activatePromoter}
        customPreviewButtonLabel="Live Preview"
        showGreenMarker={!isWebChatActivated && showGreenCueForActivate}
      />
      <div className="customer-chat-editor__content">
        <div className="customer-chat-editor__section_selectors">
          <Heading
            size={5}
            className="customer-chat-editor__section-selector-heading"
          >
            WEBSITE CHAT SETTINGS
          </Heading>

          {EDITOR_SECTIONS.map(sectionName => (
            <div
              key={sectionName}
              className={classNames('customer-chat-editor__section-selector', {
                'customer-chat-editor__section-selector--active':
                  activeEditorSection === sectionName,
              })}
              onClick={() => history.push(`#${sectionName}`)}
            >
              {sectionConfig[sectionName].title}
              {sectionConfig[sectionName].pro && (
                <RequiredPlanBadge
                  className="customer-chat-editor__plan-badge"
                  billingType={PageBillingType.Pro}
                />
              )}
            </div>
          ))}
        </div>
        <div className="customer-chat-editor__section_container">
          {activeComponent}
        </div>
        {!isMobile && activePreviewType === 'widget' && (
          <div className="customer-chat-editor__widget-preview_container">
            <CustomerChatPreview
              promoter={promoter}
              channel={channel}
              setChannel={setChannel}
            />
          </div>
        )}
      </div>
    </div>
  );
};
